import { FormRow, FormSection } from '@shared/components';
import type { Employee, Customer } from '@shared/types';
import type { LimitType, PaymentDate } from '@modules/master';
import type { CustomerCreateInput } from '../services/customer.service';

const cls = 'w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500';

interface Props {
  form: CustomerCreateInput;
  upd: (patch: Partial<CustomerCreateInput>) => void;
  employees: Employee[];
  limitTypes: LimitType[];
  paymentDates: PaymentDate[];
  customers: Customer[];
  editingCustomerId?: string | null;
}

export function CustomerFormFieldsTrade({
  form, upd, employees, limitTypes, paymentDates, customers, editingCustomerId,
}: Props) {
  const parentOptions = customers.filter((c) => c.id !== editingCustomerId);
  // 仕入先（通販業者を含む）にチェックがある場合は締め日・支払日の必須を解除する。
  const isSupplier = form.partnerTypes?.includes('supplier') ?? false;

  return (
    <FormSection title="取引条件・組織">
      <FormRow label="締め日" required={!isSupplier}>
        <select
          className={cls}
          value={form.limitTypeId ?? ''}
          required={!isSupplier}
          onChange={(e) => upd({ limitTypeId: e.target.value })}
        >
          <option value="">選択してください</option>
          {limitTypes.map((lt) => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
        </select>
      </FormRow>
      <FormRow label="支払日" required={!isSupplier}>
        <select
          className={cls}
          value={form.paymentDateId ?? ''}
          required={!isSupplier}
          onChange={(e) => upd({ paymentDateId: e.target.value })}
        >
          <option value="">選択してください</option>
          {paymentDates.map((pd) => <option key={pd.id} value={pd.id}>{pd.name}</option>)}
        </select>
      </FormRow>
      <FormRow label="支払サイト(日)">
        <input
          type="number"
          className={cls}
          value={form.paymentTerms}
          onChange={(e) => upd({ paymentTerms: Number(e.target.value) })}
        />
      </FormRow>
      <FormRow label="税計算モード">
        <select
          className={cls}
          value={form.taxCalcMode}
          onChange={(e) => upd({ taxCalcMode: e.target.value as CustomerCreateInput['taxCalcMode'] })}
        >
          <option value="floor">切捨</option>
          <option value="ceil">切上</option>
          <option value="round">四捨五入</option>
          <option value="manual">手動</option>
        </select>
      </FormRow>
      <FormRow label="親顧客">
        <select
          className={cls}
          value={form.parentId ?? ''}
          onChange={(e) => upd({ parentId: e.target.value || null })}
        >
          <option value="">本社なし</option>
          {parentOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </FormRow>
      <FormRow label="エリア">
        <input
          className={cls}
          value={form.areaId ?? ''}
          onChange={(e) => upd({ areaId: e.target.value || null })}
        />
      </FormRow>
      <FormRow label="グループ">
        <input
          className={cls}
          value={form.group ?? ''}
          onChange={(e) => upd({ group: e.target.value || null })}
        />
      </FormRow>
      <FormRow label="メール">
        <input
          type="email"
          className={cls}
          value={form.email ?? ''}
          onChange={(e) => upd({ email: e.target.value || null })}
        />
      </FormRow>
      <FormRow label="担当営業">
        <select
          className={cls}
          value={form.assignedUserId ?? ''}
          onChange={(e) => upd({ assignedUserId: e.target.value || null })}
        >
          <option value="">未割り当て</option>
          {employees.map((emp) => (
            <option key={emp.id} value={emp.id}>{emp.name}</option>
          ))}
        </select>
      </FormRow>
      <FormRow label="備考" span>
        <textarea
          rows={2}
          className={cls}
          value={form.notes ?? ''}
          onChange={(e) => upd({ notes: e.target.value || null })}
        />
      </FormRow>
    </FormSection>
  );
}
